import React, { useState, useEffect } from 'react';
import { Umbrella, RefreshCw, Plus, Edit2, Trash2, X } from 'lucide-react';
import { useTranslation } from '../../i18n/hooks/useTranslation';
import { getLeaveTypeUnitsColor, getLeaveTypePaidColor, getLeaveTypeStatusColor, getLeaveTypeCanRequestColor } from '../../utils/statusColors';

interface LeaveType {
  _id: string;
  name: string;
  typeOfUnits: 'hours' | 'days';
  isPaid: boolean;
  showOnPayslip: boolean;
  normalEntitlement?: number;
  leaveLoadingRate?: number;
  xeroLeaveTypeId?: string;
  syncedFromXero: boolean;
  isActive: boolean;
  canBeRequested: boolean;
}

export const LeaveTypes: React.FC = () => {
  const { t } = useTranslation();
  const [leaveTypes, setLeaveTypes] = useState<LeaveType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingLeaveType, setEditingLeaveType] = useState<LeaveType | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkUpdating, setBulkUpdating] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    typeOfUnits: 'days' as 'hours' | 'days',
    isPaid: true,
    showOnPayslip: true,
    canBeRequested: true,
    normalEntitlement: '',
    leaveLoadingRate: ''
  });

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  useEffect(() => {
    fetchLeaveTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchLeaveTypes = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${apiUrl}/api/leave-types`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      
      if (data.success) {
        setLeaveTypes(data.data || []);
      } else {
        setError(data.message || 'Failed to fetch leave types');
      }
    } catch (err) {
      console.error('Fetch leave types error:', err);
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (leaveType?: LeaveType) => {
    if (leaveType) {
      setEditingLeaveType(leaveType);
      setFormData({
        name: leaveType.name,
        typeOfUnits: leaveType.typeOfUnits,
        isPaid: leaveType.isPaid,
        showOnPayslip: leaveType.showOnPayslip,
        canBeRequested: leaveType.canBeRequested !== false,
        normalEntitlement: leaveType.normalEntitlement?.toString() || '',
        leaveLoadingRate: leaveType.leaveLoadingRate?.toString() || ''
      });
    } else {
      setEditingLeaveType(null);
      setFormData({
        name: '',
        typeOfUnits: 'days',
        isPaid: true,
        showOnPayslip: true,
        canBeRequested: true,
        normalEntitlement: '',
        leaveLoadingRate: ''
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingLeaveType(null);
    setFormData({
      name: '',
      typeOfUnits: 'days',
      isPaid: true,
      showOnPayslip: true,
      canBeRequested: true,
      normalEntitlement: '',
      leaveLoadingRate: ''
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const token = localStorage.getItem('token');
      const url = editingLeaveType 
        ? `${apiUrl}/api/leave-types/${editingLeaveType._id}`
        : `${apiUrl}/api/leave-types`;
      
      const method = editingLeaveType ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: formData.name,
          typeOfUnits: formData.typeOfUnits,
          isPaid: formData.isPaid,
          showOnPayslip: formData.showOnPayslip,
          canBeRequested: formData.canBeRequested,
          normalEntitlement: formData.normalEntitlement ? parseFloat(formData.normalEntitlement) : undefined,
          leaveLoadingRate: formData.leaveLoadingRate ? parseFloat(formData.leaveLoadingRate) : undefined
        })
      });

      const data = await response.json();
      
      if (data.success) {
        await fetchLeaveTypes();
        handleCloseModal();
      } else {
        setError(data.message || 'Failed to save leave type');
      }
    } catch (err) {
      console.error('Save leave type error:', err);
      setError('Failed to save leave type');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (leaveTypeId: string) => {
    if (!window.confirm('Are you sure you want to delete this leave type?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/leave-types/${leaveTypeId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      
      if (data.success) {
        await fetchLeaveTypes();
      } else {
        setError(data.message || 'Failed to delete leave type');
      }
    } catch (err) {
      console.error('Delete leave type error:', err);
      setError('Failed to delete leave type');
    }
  };

  const handleToggleActive = async (leaveType: LeaveType) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/leave-types/${leaveType._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          isActive: !leaveType.isActive
        })
      });

      const data = await response.json();
      
      if (data.success) {
        await fetchLeaveTypes();
      } else {
        setError(data.message || 'Failed to update leave type');
      }
    } catch (err) {
      console.error('Toggle active error:', err);
      setError('Failed to update leave type');
    }
  };

  const handleToggleCanBeRequested = async (leaveType: LeaveType) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/leave-types/${leaveType._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          canBeRequested: !leaveType.canBeRequested
        })
      });

      const data = await response.json();
      
      if (data.success) {
        await fetchLeaveTypes();
      } else {
        setError(data.message || 'Failed to update leave type');
      }
    } catch (err) {
      console.error('Toggle canBeRequested error:', err);
      setError('Failed to update leave type');
    }
  };

  const handleBulkToggleCanBeRequested = async (canBeRequested: boolean) => {
    if (selectedIds.size === 0) return;
    
    setBulkUpdating(true);
    try {
      const token = localStorage.getItem('token');
      const updates = Array.from(selectedIds).map(id => 
        fetch(`${apiUrl}/api/leave-types/${id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ canBeRequested })
        }).then(res => res.json())
      );

      const results = await Promise.all(updates);
      const failed = results.filter(r => !r.success);
      
      if (failed.length > 0) {
        setError(`Failed to update ${failed.length} leave type(s)`);
      } else {
        await fetchLeaveTypes();
        setSelectedIds(new Set());
      }
    } catch (err) {
      console.error('Bulk toggle canBeRequested error:', err);
      setError('Failed to update leave types');
    } finally {
      setBulkUpdating(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-600">
      <div className="p-6 border-b border-gray-200 dark:border-gray-600 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Umbrella className="w-6 h-6 text-success-600" />
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              Leave Types
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Manage employee leave types and entitlements
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {selectedIds.size > 0 && (() => {
            const selectedTypes = leaveTypes.filter(lt => selectedIds.has(lt._id));
            const allCanBeRequested = selectedTypes.every(lt => lt.canBeRequested !== false);
            
            return (
              <button
                onClick={() => handleBulkToggleCanBeRequested(!allCanBeRequested)}
                disabled={bulkUpdating}
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium cursor-pointer ${getLeaveTypeCanRequestColor(allCanBeRequested)} hover:opacity-80 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed`}
                title={`Toggle Can Request for ${selectedIds.size} selected item(s)`}
              >
                {allCanBeRequested ? 'Yes' : 'No'} ({selectedIds.size})
              </button>
            );
          })()}
          <button
            onClick={() => handleOpenModal()}
            className="flex items-center space-x-2 px-4 py-2 bg-success-600 text-white rounded-lg hover:bg-success-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>Add Leave Type</span>
          </button>
          <button
            onClick={fetchLeaveTypes}
            disabled={loading}
            className="flex items-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      <div className="p-6">
        {error && (
          <div className="mb-4 p-4 bg-red-50 dark:bg-gray-700 dark:border dark:border-red-800/50 border border-red-200 dark:border-red-800 rounded-lg text-red-800 dark:text-red-300">
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-center py-12">
            <RefreshCw className="w-8 h-8 text-gray-400 animate-spin mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400">Loading leave types...</p>
          </div>
        ) : leaveTypes.length === 0 ? (
          <div className="text-center py-12">
            <Umbrella className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400 mb-2">No leave types found</p>
            <p className="text-sm text-gray-500 dark:text-gray-500">
              {t('admin.syncFromXero') || 'Sync from Xero to import leave types'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
              <thead>
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    <input
                      type="checkbox"
                      checked={selectedIds.size > 0 && selectedIds.size === leaveTypes.length}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedIds(new Set(leaveTypes.map(lt => lt._id)));
                        } else {
                          setSelectedIds(new Set());
                        }
                      }}
                      className="w-4 h-4 text-success-600 border-gray-300 dark:border-gray-600 rounded focus:ring-green-500"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Units
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Entitlement
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Loading Rate
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Source
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Can Request
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                {leaveTypes.map((leaveType) => (
                  <tr key={leaveType._id} className={`hover:bg-gray-50 dark:hover:bg-gray-700 ${selectedIds.has(leaveType._id) ? 'bg-accent-50 dark:bg-gray-700 dark:border dark:border-accent-800/50' : ''}`}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(leaveType._id)}
                        onChange={(e) => {
                          const newSelected = new Set(selectedIds);
                          if (e.target.checked) {
                            newSelected.add(leaveType._id);
                          } else {
                            newSelected.delete(leaveType._id);
                          }
                          setSelectedIds(newSelected);
                        }}
                        className="w-4 h-4 text-success-600 border-gray-300 dark:border-gray-600 rounded focus:ring-green-500"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {leaveType.name}
                      </div>
                      {leaveType.showOnPayslip && (
                        <div className="text-xs text-gray-500 dark:text-gray-400">Shows on payslip</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${getLeaveTypeUnitsColor()}`}>
                        {leaveType.typeOfUnits}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getLeaveTypePaidColor(leaveType.isPaid)}`}>
                        {leaveType.isPaid ? 'Paid' : 'Unpaid'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                      {leaveType.normalEntitlement 
                        ? `${leaveType.normalEntitlement} ${leaveType.typeOfUnits}/year`
                        : 'Not set'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                      {leaveType.leaveLoadingRate ? `${leaveType.leaveLoadingRate}%` : '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {leaveType.syncedFromXero ? (
                        <span className="inline-flex items-center text-xs text-accent-600 dark:text-accent-400">
                          <img src="/xero.svg" alt="Xero" className="w-4 h-4 mr-1" />
                          Xero
                        </span>
                      ) : (
                        <span className="text-xs text-gray-500 dark:text-gray-400">Manual</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleToggleActive(leaveType)}
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium cursor-pointer ${getLeaveTypeStatusColor(leaveType.isActive)} hover:opacity-80 transition-opacity`}
                      >
                        {leaveType.isActive ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleToggleCanBeRequested(leaveType)}
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium cursor-pointer ${getLeaveTypeCanRequestColor(leaveType.canBeRequested !== false)} hover:opacity-80 transition-opacity`}
                        title={leaveType.canBeRequested !== false ? 'Can be requested by staff' : 'Cannot be requested (e.g., Holiday Pay)'}
                      >
                        {leaveType.canBeRequested !== false ? 'Yes' : 'No'}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => handleOpenModal(leaveType)}
                          className="p-1 text-accent-600 hover:bg-accent-50 dark:hover:bg-accent-900/20 rounded"
                          title={leaveType.syncedFromXero ? "Edit (limited fields for Xero-synced types)" : "Edit"}
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        {!leaveType.syncedFromXero && (
                          <button
                            onClick={() => handleDelete(leaveType._id)}
                            className="p-1 text-error-600 hover:bg-error-50 dark:hover:bg-error-900/20 rounded"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-600">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {editingLeaveType ? 'Edit Leave Type' : 'Create Leave Type'}
              </h3>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {editingLeaveType?.syncedFromXero && (
                <div className="bg-accent-50 dark:bg-gray-700 dark:border dark:border-accent-800/50 border border-accent-200 dark:border-accent-800 rounded-lg p-3">
                  <p className="text-sm text-accent-800 dark:text-accent-300">
                    This leave type is synced from Xero. Only entitlement and status can be modified.
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  disabled={editingLeaveType?.syncedFromXero}
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 disabled:bg-gray-100 dark:disabled:bg-gray-600 disabled:cursor-not-allowed"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Type of Units *
                </label>
                <select
                  value={formData.typeOfUnits}
                  onChange={(e) => setFormData({ ...formData, typeOfUnits: e.target.value as 'hours' | 'days' })}
                  disabled={editingLeaveType?.syncedFromXero}
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 disabled:bg-gray-100 dark:disabled:bg-gray-600 disabled:cursor-not-allowed"
                >
                  <option value="days">Days</option>
                  <option value="hours">Hours</option>
                </select>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="isPaid"
                  checked={formData.isPaid}
                  onChange={(e) => setFormData({ ...formData, isPaid: e.target.checked })}
                  disabled={editingLeaveType?.syncedFromXero}
                  className="w-4 h-4 text-success-600 border-gray-300 dark:border-gray-600 rounded focus:ring-green-500 disabled:cursor-not-allowed"
                />
                <label htmlFor="isPaid" className="text-sm text-gray-700 dark:text-gray-300">
                  Paid Leave
                </label>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="showOnPayslip"
                  checked={formData.showOnPayslip}
                  onChange={(e) => setFormData({ ...formData, showOnPayslip: e.target.checked })}
                  disabled={editingLeaveType?.syncedFromXero}
                  className="w-4 h-4 text-success-600 border-gray-300 dark:border-gray-600 rounded focus:ring-green-500 disabled:cursor-not-allowed"
                />
                <label htmlFor="showOnPayslip" className="text-sm text-gray-700 dark:text-gray-300">
                  Show on Payslip
                </label>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="canBeRequested"
                  checked={formData.canBeRequested}
                  onChange={(e) => setFormData({ ...formData, canBeRequested: e.target.checked })}
                  className="w-4 h-4 text-success-600 border-gray-300 dark:border-gray-600 rounded focus:ring-green-500"
                />
                <label htmlFor="canBeRequested" className="text-sm text-gray-700 dark:text-gray-300">
                  Can be Requested by Staff
                </label>
                <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                  (Uncheck for types like Holiday Pay that are paid out, not requested)
                </span>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Annual Entitlement ({formData.typeOfUnits})
                </label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  value={formData.normalEntitlement}
                  onChange={(e) => setFormData({ ...formData, normalEntitlement: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Leave Loading Rate (%)
                </label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="100"
                  value={formData.leaveLoadingRate}
                  onChange={(e) => setFormData({ ...formData, leaveLoadingRate: e.target.value })}
                  disabled={editingLeaveType?.syncedFromXero}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 disabled:bg-gray-100 dark:disabled:bg-gray-600 disabled:cursor-not-allowed"
                />
              </div>

              <div className="flex items-center space-x-3 pt-4">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 px-4 py-2 bg-success-600 text-white rounded-lg hover:bg-success-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Saving...' : editingLeaveType ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

